import { useMemo, useState } from 'react';
import { WebLayout } from 'components/Layout';
import { OrderSummary } from '../components/OrderSummary';
import { DiscountCode } from '../components/DiscountCode';
import { PaymentMethod, MERCHANT_CODE } from '../components/PaymentMethod';
import { formatUGX, buildWaLink } from '../api/getProducts';
import { useCart } from 'context/CartContext';

export function Payment() {
  const { items, subtotal, clearCart } = useCart();
  const [discountResult, setDiscountResult] = useState(null);
  const [method, setMethod] = useState('momo');

  const discountAmount = discountResult?.amount || 0;
  const total = Math.max(subtotal - discountAmount, 0);

  const confirmLink = useMemo(() => {
    if (!items.length) return buildWaLink('Hi Chikwafu, I want to complete my order');

    const lines = items
      .map((item) => `• ${item.name} x${item.qty} — UGX ${formatUGX(item.price * item.qty)}`)
      .join('\n');

    const discountLine = discountResult
      ? `\nDiscount (${discountResult.discount.code}): -UGX ${formatUGX(discountAmount)}`
      : '';

    const methodLine =
      method === 'momo'
        ? `Payment: MTN MoMo merchant ${MERCHANT_CODE} (I will share the confirmation screenshot)`
        : 'Payment: Pay on delivery';

    const message =
      `Hi Chikwafu, I'd like to confirm my order:\n\n${lines}\n\n` +
      `Subtotal: UGX ${formatUGX(subtotal)}${discountLine}\n` +
      `Total: UGX ${formatUGX(total)}\n\n${methodLine}`;

    return buildWaLink(message);
  }, [items, subtotal, total, discountResult, discountAmount, method]);

  return (
    <WebLayout>
      <section className="mx-auto max-w-3xl px-4 py-16 sm:px-8">
        <div className="mb-10 text-center">
          <div className="mb-3 text-xs font-extrabold uppercase tracking-[0.2em] text-accent">
            Checkout
          </div>
          <h1 className="text-[clamp(1.8rem,4vw,2.5rem)] font-black tracking-tight">
            Complete your payment
          </h1>
          <p className="mt-2 text-sm text-text-muted">
            Review your order, apply a discount code if you have one, then choose how you&apos;d
            like to pay.
          </p>
        </div>

        {items.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="flex flex-col gap-5">
            <OrderSummary items={items} />

            <DiscountCode
              subtotal={subtotal}
              applied={discountResult}
              onApply={setDiscountResult}
              onRemove={() => setDiscountResult(null)}
            />

            <PaymentMethod method={method} onChange={setMethod} />

            <div className="rounded-2xl border border-white/10 bg-card p-5">
              <div className="flex flex-col gap-2 text-sm">
                <div className="flex items-center justify-between text-text-muted">
                  <span>Subtotal</span>
                  <span className="text-text">UGX {formatUGX(subtotal)}</span>
                </div>
                {discountResult && (
                  <div className="flex items-center justify-between text-accent">
                    <span>Discount ({discountResult.discount.code})</span>
                    <span>-UGX {formatUGX(discountAmount)}</span>
                  </div>
                )}
                <div className="mt-1 flex items-center justify-between border-t border-white/10 pt-3 text-base font-extrabold">
                  <span>Total</span>
                  <span className="text-accent">UGX {formatUGX(total)}</span>
                </div>
              </div>

              <a
                href={confirmLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={clearCart}
                className="mt-5 block w-full rounded-full bg-accent py-3 text-center text-sm font-extrabold text-black transition-all hover:-translate-y-0.5 hover:bg-accent-dim"
              >
                Confirm order on WhatsApp
              </a>
              <p className="mt-3 text-center text-xs text-text-dim">
                We&apos;ll confirm your order and dispatch within 3 working days.
              </p>
            </div>
          </div>
        )}
      </section>
    </WebLayout>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center gap-4 rounded-2xl border border-dashed border-white/10 py-16 text-center">
      <p className="text-text-muted">Your cart is empty, so there&apos;s nothing to pay for yet.</p>
      <a
        href="/shop"
        className="rounded-full bg-accent px-6 py-2.5 text-sm font-extrabold text-black transition-all hover:-translate-y-0.5 hover:bg-accent-dim"
      >
        Browse the catalog
      </a>
    </div>
  );
}
